import { useState } from 'react';
import axios from 'axios';
import C from '../../constants/palette';

export default function TrackingPage({ onBack }) {
  const params = new URLSearchParams(window.location.search);
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [trackingId, setTrackingId] = useState(params.get('trackingId') || params.get('paymentId') || '');
  const [code, setCode] = useState('');
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [message, setMessage] = useState('');

  const sendCode = async (e) => {
    e.preventDefault();
    setErr('');
    setMessage('');
    setLoading(true);
    try {
      await axios.post('/api/tracking/send-code', { email, trackingId });
      setMessage('A verification code has been sent to your email.');
      setStep('verify');
    } catch (error) {
      setErr(error.response?.data?.error || 'Unable to find that transaction.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const res = await axios.post('/api/tracking/verify', { email, trackingId, code });
      setTransaction(res.data);
      setStep('detail');
    } catch (error) {
      setErr(error.response?.data?.error || 'Invalid or expired verification code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FB', color: C.ink, fontFamily: 'DM Sans' }}>
      <header style={{ height: 86, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6vw', background: '#fff', borderBottom: `1px solid ${C.border}` }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'baseline', gap: 16 }}>
          <span style={{ fontFamily: 'Playfair Display', fontSize: 30, color: C.ink, letterSpacing: '0.02em' }}>KINGSTRADE</span>
          <span style={{ fontSize: 11, color: C.muted, letterSpacing: '0.18em', fontWeight: 600 }}>TRANSACTION TRACKING</span>
        </button>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 11, letterSpacing: '0.18em', fontWeight: 600 }}>
          BACK TO HOME
        </button>
      </header>

      <main style={{ maxWidth: 980, margin: '0 auto', padding: '84px 24px' }}>
        <p style={{ color: C.gold, fontSize: 11, letterSpacing: '0.28em', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center', marginBottom: 18 }}>
          Private Acquisition Record
        </p>
        <h1 style={{ fontFamily: 'Playfair Display', fontSize: 'clamp(42px, 6vw, 72px)', lineHeight: 1.02, fontWeight: 400, textAlign: 'center', marginBottom: 18 }}>
          Track your treasure transaction
        </h1>
        <p style={{ maxWidth: 620, margin: '0 auto 44px', textAlign: 'center', color: C.muted, fontSize: 16, lineHeight: 1.8 }}>
          Enter the email used at checkout and the tracking ID from your purchase confirmation email.
        </p>

        <section style={{ maxWidth: 560, margin: '0 auto', background: '#fff', border: `1px solid ${C.border}`, padding: 32, boxShadow: '0 20px 60px rgba(15,23,42,0.06)' }}>
          {step === 'request' && (
            <form onSubmit={sendCode}>
              <Field label="Email">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="collector@example.com" required style={inputStyle} />
              </Field>
              <Field label="Tracking ID">
                <input value={trackingId} onChange={e => setTrackingId(e.target.value)} placeholder="Payment ID" required style={inputStyle} />
              </Field>
              {err && <Notice error>{err}</Notice>}
              {message && <Notice>{message}</Notice>}
              <ActionButton loading={loading}>{loading ? 'Sending code...' : 'Send verification code'}</ActionButton>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={verifyCode}>
              <Field label="Verification Code">
                <input value={code} onChange={e => setCode(e.target.value)} placeholder="4-digit code" required style={inputStyle} />
              </Field>
              {err && <Notice error>{err}</Notice>}
              {message && <Notice>{message}</Notice>}
              <ActionButton loading={loading}>{loading ? 'Verifying...' : 'View transaction detail'}</ActionButton>
              <button type="button" onClick={() => { setStep('request'); setErr(''); }} style={secondaryStyle}>Use another email or tracking ID</button>
            </form>
          )}

          {step === 'detail' && transaction && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
                <div>
                  <p style={{ color: C.gold, fontSize: 10, letterSpacing: '0.18em', fontWeight: 700, marginBottom: 6 }}>TRANSACTION VERIFIED</p>
                  <h2 style={{ fontFamily: 'Playfair Display', fontSize: 28, fontWeight: 400, color: C.ink }}>{transaction.treasureName}</h2>
                </div>
                <span style={{ border: `1px solid ${C.border}`, padding: '8px 10px', fontFamily: 'DM Mono', fontSize: 11, color: C.ink }}>{transaction.paymentStatus}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  ['Treasure ID', transaction.treasureId],
                  ['Item Status', transaction.itemStatus],
                  ['Amount', `${transaction.currency} $${Number(transaction.amount || 0).toLocaleString()}`],
                  ['Buyer', transaction.buyerName],
                  ['Contact', transaction.contactNumber],
                  ['Fulfillment', transaction.fulfillmentType === 'DELIVERY' ? 'Delivery' : transaction.fulfillmentType === 'SELF_PICKUP' ? 'Self pickup' : '—'],
                  ['Delivery Address', transaction.deliveryAddress || '—'],
                  ['Provider', transaction.provider],
                  ['Provider Transaction', transaction.providerPaymentId || '—'],
                  ['Payment ID', transaction.paymentId],
                  ['Order ID', transaction.orderId],
                  ['Updated', transaction.paidAt ? new Date(transaction.paidAt).toLocaleString('en-GB') : '—'],
                ].map(([label, value]) => (
                  <div key={label} style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                    <p style={{ color: C.muted, fontSize: 9, letterSpacing: '0.16em', fontWeight: 700, marginBottom: 5 }}>{label.toUpperCase()}</p>
                    <p style={{ color: C.ink, fontFamily: 'DM Mono', fontSize: 12, wordBreak: 'break-all' }}>{String(value || '—')}</p>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => { setStep('request'); setTransaction(null); setCode(''); }} style={{ ...secondaryStyle, marginTop: 28 }}>
                Track another transaction
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 22 }}>
      <span style={{ display: 'block', color: C.muted, fontSize: 10, letterSpacing: '0.18em', fontWeight: 700, marginBottom: 8 }}>{label.toUpperCase()}</span>
      {children}
    </label>
  );
}

function Notice({ error, children }) {
  return (
    <div style={{
      padding: '11px 13px',
      border: `1px solid ${error ? 'rgba(224,49,49,0.25)' : 'rgba(42,157,92,0.25)'}`,
      background: error ? 'rgba(224,49,49,0.08)' : 'rgba(42,157,92,0.08)',
      color: error ? '#e03131' : '#2a9d5c',
      fontSize: 12,
      marginBottom: 18,
    }}>
      {children}
    </div>
  );
}

function ActionButton({ loading, children }) {
  return (
    <button type="submit" disabled={loading} style={{
      width: '100%',
      border: 'none',
      background: loading ? '#b89520' : C.gold,
      color: C.dark,
      padding: '14px 18px',
      fontSize: 11,
      letterSpacing: '0.16em',
      fontWeight: 700,
      textTransform: 'uppercase',
      cursor: loading ? 'wait' : 'pointer',
    }}>
      {children}
    </button>
  );
}

const inputStyle = {
  width: '100%',
  border: `1px solid ${C.border}`,
  padding: '13px 14px',
  fontSize: 14,
  color: C.ink,
  outline: 'none',
  fontFamily: 'DM Mono',
  background: '#fff',
};

const secondaryStyle = {
  width: '100%',
  marginTop: 12,
  border: `1px solid ${C.border}`,
  background: '#fff',
  color: C.muted,
  padding: '12px 14px',
  fontSize: 10,
  letterSpacing: '0.14em',
  fontWeight: 700,
  textTransform: 'uppercase',
  cursor: 'pointer',
};
